import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { TransformControls } from '@react-three/drei';
import { useAppStore, useTool } from '../state/store';
import { useBIMStore } from '../state/useBIMStore';
import { useSketchStore } from '../state/useSketchStore';
import { useCommandInteract } from '../core/commands/commandInteract';
import { useGumballStore, registerGumballControls } from '../state/useGumballStore';
import {
    transformSceneObjects, sceneSelectionAnchor, sketchFaceSelectionOf, sketchEdgeSelectionOf,
    sketchVertexSelectionOf,
} from '../core/scene/editRouter';
import {
    faceSelectionId, edgeSelectionId, vertexSelectionId,
    parseEdgeSelectionId, parseVertexSelectionId, type SketchSolid,
} from '../core/sketch/types';
import { pointKey, solidSharedEdge, edgeChainKeys, solidVertexPoints } from '../core/sketch/topology';
import { transformSolid, transformSolidSubobjects } from '../core/sketch/transform';
import { frameSectionPlane } from '../core/sketch/sectionPlane';
import { SketchGhostSolidPreview } from '../tools/sketch/previews';

/**
 * GumballLayer (R5, B4 cross-workbench upgrade): the 3D-style manipulator.
 * A drei TransformControls gizmo sits centered on the selection's bounds —
 * aligned to the picked object's own frame when exactly one object is
 * selected (face normal, edge/solid run, wall base curve), world-aligned
 * for multi-selection — and on drag-end the accumulated matrix is routed
 * through the cross-workbench edit router, so CAD entities, BIM elements
 * and sketch solids — a mixed selection included — all bake the transform
 * into their OWNING store (undo/redo and the ledger mirror stay intact;
 * the Gumball "writes through the transform API" contract of the plan).
 *
 * Face selections (`solidId:faceId` composites from the sketch workbench)
 * manipulate the SUBOBJECT: the gizmo anchors on the picked face's contour
 * with Z riding the face normal, and the commit stretches the solid around
 * it — the parent solid in the app selection never rides along.
 *
 * While a command interaction is active or a drawing tool runs, the
 * gizmo hides: the viewport pointer belongs to the command/tool.
 *
 * The section-plane entity (picked through its marker skin) manipulates
 * the PLANE: the gizmo anchors on the plane's frame and the commit bakes
 * the drag delta into the plane equation (normal + constant).
 */
export const GumballLayer = memo(function GumballLayer() {
    const selectedIds = useAppStore((s) => s.selectedIds);
    // Reactive change tokens for the owning stores: any of them rewriting a
    // selected object (or the selection) recomputes the anchor centroid.
    const entities = useAppStore((s) => s.entities);
    const bimElements = useBIMStore((s) => s.project.elements);
    const sketchSolids = useSketchStore((s) => s.solids);
    const sketchSelection = useSketchStore((s) => s.selectedIds);
    const sectionPlane = useSketchStore((s) => s.sectionPlane);
    const sectionPlaneSelected = useSketchStore((s) => s.sectionPlaneSelected);
    const tool = useTool();
    const interactActive = useCommandInteract((s) => s.active);
    const enabled = useGumballStore((s) => s.enabled);
    const mode = useGumballStore((s) => s.mode);
    const proxyRef = useRef<THREE.Object3D>(null);
    const startMatrix = useRef(new THREE.Matrix4());
    // Raw TransformControls handle (axis/dragging) — registered so viewport
    // pick contracts can stand down while the gizmo owns the pointer.
    const controlsRef = useRef<{ axis: string | null; dragging: boolean } | null>(null);
    // Live preview: the accumulated delta while the drag is active. The
    // gizmo only moves its proxy — the model itself must follow visually
    // (ghost) or nothing appears to happen until mouse-up commit.
    const [dragMatrix, setDragMatrix] = useState<THREE.Matrix4 | null>(null);

    // Gizmo targets: face/edge/vertex composites manipulate the subobject
    // inside their solid; the parent solid the sketch select puts into the
    // app selection is dropped so the gesture moves the subobject, not the
    // whole mass.
    const targets = useMemo(() => {
        const composites: string[] = [];
        const subobjectParents = new Set<string>();
        sketchSelection.forEach((id) => {
            const face = sketchFaceSelectionOf(id);
            if (face) {
                composites.push(faceSelectionId(face.solidId, face.faceId));
                subobjectParents.add(face.solidId);
                return;
            }
            const edge = sketchEdgeSelectionOf(id);
            if (edge) {
                composites.push(edgeSelectionId(edge.solidId, edge.edgeKey));
                subobjectParents.add(edge.solidId);
                return;
            }
            const vertex = sketchVertexSelectionOf(id);
            if (vertex) {
                composites.push(vertexSelectionId(vertex.solidId, vertex.vertexKey));
                subobjectParents.add(vertex.solidId);
            }
        });
        return [...composites, ...selectedIds.filter((id) => !subobjectParents.has(id))];
        // `sketchSolids` keeps face validity reactive (a deleted solid drops
        // its composite from the targets).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sketchSelection, selectedIds, sketchSolids]);

    // Section-plane entity selection: the gizmo anchors ON the plane skin
    // (the same framing the marker renders and the user picks) with the
    // plane's orientation, so the rotate rings turn the section around its
    // own axes. Commit-only — the drag delta bakes into the plane equation
    // on mouse-up (the BIM/CAD flow; live re-anchoring would fight the
    // gesture, since the anchor itself moves with the plane).
    const planeAnchor = useMemo(() => {
        if (!sectionPlaneSelected || !sectionPlane) return null;
        return frameSectionPlane(sectionPlane, sketchSolids);
    }, [sectionPlaneSelected, sectionPlane, sketchSolids]);

    // Anchor pose: the selection's bounding-box center — the visual center,
    // which a point average skews off under uneven anchor sampling — and,
    // for a single-object pick, the object's own frame (see
    // sceneSelectionAnchor). `entities`/`bimElements` are the reactivity
    // carriers; the router reads the live stores itself.
    const anchor = useMemo<{ position: THREE.Vector3; quaternion: THREE.Quaternion } | null>(() => {
        if (planeAnchor) return { position: planeAnchor.center, quaternion: planeAnchor.quaternion };
        if (targets.length === 0) return null;
        return sceneSelectionAnchor(targets);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targets, planeAnchor, entities, bimElements, sketchSolids]);

    // reset the proxy pose whenever the selection/anchor changes
    useEffect(() => {
        const proxy = proxyRef.current;
        if (!proxy || !anchor) return;
        proxy.position.copy(anchor.position);
        proxy.quaternion.copy(anchor.quaternion);
        proxy.scale.set(1, 1, 1);
        proxy.updateMatrixWorld(true);
    }, [anchor, targets]);

    const show = enabled && !!anchor && !interactActive && tool === 'select';

    // Register/unregister the raw handle around mount/unmount: the sketch
    // bus checks `gumballOwnsPointer()` on every dispatched hit.
    useEffect(() => {
        registerGumballControls(controlsRef.current);
        return () => registerGumballControls(null);
    }, [show]);

    // Live ghost targets: sketch citizens (whole solids, face/edge/vertex
    // composites) deformed under the CURRENT drag delta — the same engine
    // the commit path runs. BIM/CAD targets keep their commit-only flow.
    const previewSolids = useMemo(() => {
        if (!dragMatrix || planeAnchor) return [] as Array<{ key: string; solid: SketchSolid }>;
        const subobjects = new Map<string, { faceIds: string[]; edgeKeys: string[]; vertexKeys: string[] }>();
        const wholeIds: string[] = [];
        targets.forEach((id) => {
            const face = sketchFaceSelectionOf(id);
            if (face) {
                const entry = subobjects.get(face.solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                if (!entry.faceIds.includes(face.faceId)) entry.faceIds.push(face.faceId);
                subobjects.set(face.solidId, entry);
                return;
            }
            const edge = sketchEdgeSelectionOf(id);
            if (edge) {
                const solid = useSketchStore.getState().solids[edge.solidId];
                const entry = subobjects.get(edge.solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                (solid ? edgeChainKeys(solid, edge.edgeKey) : [edge.edgeKey]).forEach((key) => {
                    if (!entry.edgeKeys.includes(key)) entry.edgeKeys.push(key);
                });
                subobjects.set(edge.solidId, entry);
                return;
            }
            const vertex = sketchVertexSelectionOf(id);
            if (vertex) {
                const entry = subobjects.get(vertex.solidId) ?? { faceIds: [], edgeKeys: [], vertexKeys: [] };
                if (!entry.vertexKeys.includes(vertex.vertexKey)) entry.vertexKeys.push(vertex.vertexKey);
                subobjects.set(vertex.solidId, entry);
                return;
            }
            if (useSketchStore.getState().solids[id]) wholeIds.push(id);
        });
        const out: Array<{ key: string; solid: SketchSolid }> = [];
        subobjects.forEach(({ faceIds, edgeKeys, vertexKeys }, solidId) => {
            const solid = useSketchStore.getState().solids[solidId];
            if (solid) {
                out.push({
                    key: `${solidId}:sub`,
                    solid: transformSolidSubobjects(solid, faceIds, edgeKeys, dragMatrix, vertexKeys),
                });
            }
        });
        wholeIds.forEach((id) => {
            const solid = useSketchStore.getState().solids[id];
            if (solid) out.push({ key: `${id}:whole`, solid: transformSolid(solid, dragMatrix) });
        });
        return out;
        // `targets` is derived from the selection; recomputing per drag
        // frame is the point — the delta IS the preview.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragMatrix, targets, planeAnchor]);

    // STABLE proxy child + ref: drei's TransformControls re-attaches its
    // object whenever the `children` dep changes identity — a new element
    // every preview frame would detach/attach MID-DRAG and reset the
    // gesture, so the gizmo drag must never re-create them. (Hooks stay
    // above the early return: mount/unmount must not change hook order.)
    const handleControlsRef = useCallback((instance: unknown) => {
        // Keep a structural handle (axis/dragging) without coupling this
        // module to the three-stdlib typings.
        controlsRef.current = instance as { axis: string | null; dragging: boolean } | null;
    }, []);

    const proxyChild = useMemo(
        // Runs on EVERY render (hooks sit above the early return), and the
        // selection can be empty while the layer is mounted: a null
        // anchor must not crash the factory. The pose reset effect
        // repositions the proxy from the real anchor the moment the
        // gizmo becomes visible.
        () => (
            <object3D
                ref={proxyRef}
                position={(anchor?.position.toArray() ?? [0, 0, 0]) as [number, number, number]}
            />
        ),
        [anchor]
    );

    if (!show) return null;

    // Scale is meaningless for an infinite plane — translate stands in.
    const effectiveMode = planeAnchor && mode === 'scale' ? 'translate' : mode;

    const commit = () => {
        const proxy = proxyRef.current;
        if (!proxy || !anchor) return;
        const delta = new THREE.Matrix4().copy(proxy.matrixWorld).multiply(startMatrix.current.clone().invert());
        const moved = !delta.equals(new THREE.Matrix4());
        proxy.position.copy(anchor.position);
        proxy.quaternion.copy(anchor.quaternion);
        proxy.scale.set(1, 1, 1);
        if (!moved) return;
        if (planeAnchor) {
            if (!sectionPlane) return;
            // Bake the drag into the plane equation: three's
            // Plane.applyMatrix4 re-derives the rotated normal and the
            // translated constant from the rigid delta in one step.
            const plane = new THREE.Plane(
                new THREE.Vector3(...sectionPlane.normal).normalize(),
                sectionPlane.constant
            ).applyMatrix4(delta);
            useSketchStore.getState().setSectionPlane({
                normal: [plane.normal.x, plane.normal.y, plane.normal.z],
                constant: plane.constant,
            });
            return;
        }
        // Snapshot edge AND vertex composites BEFORE the transform: their
        // keys are position-derived, so the sketch selection is re-keyed
        // onto the transformed topology afterwards — the gizmo stays alive
        // for the next gesture instead of dying with the stale key.
        const pointKeyOf = (v: THREE.Vector3): string => pointKey([v.x, v.y, v.z]);
        const edgeSnapshots = targets.flatMap((id) => {
            const parsed = parseEdgeSelectionId(id);
            if (!parsed) return [];
            const solid = useSketchStore.getState().solids[parsed.solidId];
            const edge = solid ? solidSharedEdge(solid, parsed.edgeKey) : null;
            if (!edge) return [];
            const ka = pointKeyOf(edge.a.clone().applyMatrix4(delta));
            const kb = pointKeyOf(edge.b.clone().applyMatrix4(delta));
            return [{
                solidId: parsed.solidId,
                originalKey: parsed.edgeKey,
                nextKey: ka < kb ? `${ka}~${kb}` : `${kb}~${ka}`,
            }];
        });
        const vertexSnapshots = targets.flatMap((id) => {
            const parsed = parseVertexSelectionId(id);
            if (!parsed) return [];
            const solid = useSketchStore.getState().solids[parsed.solidId];
            const pts = solid ? solidVertexPoints(solid, parsed.vertexKey) : [];
            if (pts.length === 0) return [];
            return [{
                solidId: parsed.solidId,
                originalKey: parsed.vertexKey,
                nextKey: pointKeyOf(new THREE.Vector3(...pts[0]).applyMatrix4(delta)),
            }];
        });
        transformSceneObjects(targets, delta, 'Gumball Transform');
        const sketches = useSketchStore.getState();
        const rekey = (
            snapshots: Array<{ solidId: string; originalKey: string; nextKey: string }>,
            parse: (id: string) => { solidId: string; key: string } | null,
            compose: (solidId: string, key: string) => string,
            live: (solid: SketchSolid, key: string) => boolean
        ): void => {
            if (snapshots.length === 0) return;
            let changed = false;
            const nextIds = sketches.selectedIds.map((id) => {
                const parsed = parse(id);
                if (!parsed) return id;
                const snap = snapshots.find(
                    (s) => s.solidId === parsed.solidId && s.originalKey === parsed.key
                );
                if (!snap) return id;
                const after = useSketchStore.getState().solids[parsed.solidId];
                if (after && live(after, snap.nextKey)) {
                    changed = true;
                    return compose(parsed.solidId, snap.nextKey);
                }
                return id;
            });
            if (changed) useSketchStore.getState().select(nextIds);
        };
        rekey(
            edgeSnapshots,
            (id) => {
                const parsed = parseEdgeSelectionId(id);
                return parsed ? { solidId: parsed.solidId, key: parsed.edgeKey } : null;
            },
            edgeSelectionId,
            (solid, key) => !!solidSharedEdge(solid, key)
        );
        rekey(
            vertexSnapshots,
            (id) => {
                const parsed = parseVertexSelectionId(id);
                return parsed ? { solidId: parsed.solidId, key: parsed.vertexKey } : null;
            },
            vertexSelectionId,
            (solid, key) => solidVertexPoints(solid, key).length > 0
        );
    };

    return (
        <group>
            {/* Live preview while dragging: the deformed sketch geometry
                follows the proxy 1:1 (same weld engine as the commit). */}
            {previewSolids.map(({ key, solid }) => (
                <SketchGhostSolidPreview key={key} solid={solid} matrix={IDENTITY_MATRIX} />
            ))}
            <TransformControls
                ref={handleControlsRef}
                mode={effectiveMode}
                size={0.75}
                // The gizmo renders in the proxy's own orientation (the
                // anchor frame above); three's 'world' default would draw
                // world-aligned axes and ignore it.
                space="local"
                onMouseDown={() => {
                    const proxy = proxyRef.current;
                    if (proxy) startMatrix.current.copy(proxy.matrixWorld);
                    setDragMatrix(null);
                }}
                onObjectChange={() => {
                    const proxy = proxyRef.current;
                    if (!proxy) return;
                    const delta = new THREE.Matrix4()
                        .copy(proxy.matrixWorld)
                        .multiply(startMatrix.current.clone().invert());
                    setDragMatrix(delta);
                }}
                onMouseUp={() => {
                    commit();
                    setDragMatrix(null);
                }}
            >
                {proxyChild}
            </TransformControls>
        </group>
    );
});

/** Shared identity transform for highlight-only ghost previews. */
const IDENTITY_MATRIX = new THREE.Matrix4();
